/**
 * CORE-2052 — `RangeError: Maximum call stack size exceeded` из чужого скрипта.
 *
 * GlitchTip frontend 105 (прод) и 112 (dev), Edge 156 / Windows: все кадры
 * стека — `<anonymous>:1`, функция `Object.getOwnPropertyDescriptor [as
 * _getOwnPropertyDescriptor]` вызывает сама себя. Такого имени нет ни в
 * нашем коде, ни в node_modules: это скрипт, внедрённый в страницу
 * (расширение браузера), который подменил `Object.getOwnPropertyDescriptor`
 * обёрткой и обернул её второй раз. После этого любой вызов дескриптора на
 * странице — наш, React, чей угодно — уходит в рекурсию; React даже
 * componentStack построить не может («Error generating stack»).
 *
 * Починить чужое расширение нельзя, поэтому событие не отправляем. Признак
 * узкий: RangeError про переполнение стека, кадры только `<anonymous>`, и
 * среди них — подменённый `_getOwnPropertyDescriptor`. Любая наша рекурсия
 * (кадры из файлов бандла) проходит как раньше.
 */

interface FrameLike {
  filename?: string;
  abs_path?: string;
  function?: string;
}

interface ExceptionLike {
  type?: string;
  value?: string;
  stacktrace?: { frames?: FrameLike[] };
}

const ANONYMOUS_FILE = /(^|\/)<anonymous>$/;

function isAnonymousFrame(frame: FrameLike): boolean {
  const file = frame?.filename ?? frame?.abs_path ?? '';
  return ANONYMOUS_FILE.test(file);
}

export function isForeignDescriptorRecursionEvent(event: unknown): boolean {
  const values = (event as { exception?: { values?: ExceptionLike[] } } | null)?.exception?.values;
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.some((ex) => {
    if (ex?.type !== 'RangeError') return false;
    if (!/Maximum call stack size exceeded/i.test(ex?.value ?? '')) return false;
    const frames = ex?.stacktrace?.frames;
    if (!Array.isArray(frames) || frames.length === 0) return false;
    if (!frames.every(isAnonymousFrame)) return false;
    return frames.some((f) => /\b_getOwnPropertyDescriptor\b/.test(f?.function ?? ''));
  });
}

/** `beforeSend` step: `null` for {@link isForeignDescriptorRecursionEvent}, else the event. */
export function dropForeignDescriptorRecursion<T>(event: T): T | null {
  if (event === null) return null;
  return isForeignDescriptorRecursionEvent(event) ? null : event;
}
